import React, { memo } from "react"
import { Link } from "gatsby"

/**
 * S4 — "What you are probably thinking" (LANDING-PLAN §4).
 *
 * HANDOFF: S3 leaves the reader thinking "those are other people's businesses
 * — what are the risks for mine?". This answers the four questions a buyer
 * will not raise on a first call, before they have to.
 *
 * Every claim here is already live elsewhere on the site — the OriginStory
 * section states no client has had to start over, that the team which begins a
 * project finishes it, 100% founder involvement, and multi-year active US
 * relationships. Nothing here is a new claim (CONTENT-ARCHITECTURE §0).
 *
 * Not an accordion: hiding an objection behind a click defeats the point of
 * raising it unprompted.
 */
/**
 * The clients named in objection 1.
 *
 * `caseStudy` is an internal route and renders as a normal <Link>. `site` is the
 * client's own website and opens in a new tab — used only where we have no case
 * study to send the reader to, so the name is still checkable.
 *
 * NOTE: components/ServiceProof.js and templates/offer.js both state that the
 * PROOF attribution is "never linked to a client's own website". That rule is
 * about the proof construction, where an outbound link would pull the reader
 * off-site at the moment of evidence. This is a different device: here an
 * outbound link is the only way to evidence a client we have not written up.
 *
 * A client with neither a caseStudy nor a site renders as plain text — never as
 * a dead or guessed link.
 */
const CLIENTS = {
  bigJerrys: {
    name: "Big Jerry’s Fencing",
    caseStudy: "/caseStudies/projects/bigJerrysFencing",
  },
  orr: {
    name: "ORR Protection",
    site: "https://orrprotection.com/",
  },
  renewell: {
    name: "Renewell Water",
    site: "https://www.renewellwater.co.uk/",
  },
  nurtureful: {
    name: "Nurtureful Psychology",
    caseStudy: "/caseStudies/projects/nurturefulPsychology",
  },
}

const ClientName = ({ client }) => {
  if (client.caseStudy) {
    return <Link to={client.caseStudy}>{client.name}</Link>
  }
  if (client.site) {
    return (
      <a href={client.site} target="_blank" rel="noopener noreferrer">
        {client.name}
      </a>
    )
  }
  return <>{client.name}</>
}

const ClientRoll = () => (
  <>
    Several of the client relationships behind this site are multi-year and
    still running: <ClientName client={CLIENTS.bigJerrys} />,{" "}
    <ClientName client={CLIENTS.orr} /> and{" "}
    <ClientName client={CLIENTS.nurtureful} /> in the US, and{" "}
    <ClientName client={CLIENTS.renewell} /> in the UK. Where a case study
    exists it names the outcomes they produced. If we get to a serious
    conversation, we will put you in touch with them.
  </>
)

const OBJECTIONS = [
  {
    q: "Can a team in Pakistan deliver for a business in the US or UK?",
    // Deliberately does NOT offer a reference call. References are provided on
    // request once a prospect is qualified, not advertised to cold traffic.
    // The answer stands on things already public — the case studies name the
    // clients and their outcomes — and the closing clause signals that
    // references exist without promising a call, a count, or an unaccompanied
    // one. Do not reinstate any earlier wording that offered one.
    // THE ANSWER NAMES CLIENTS ON BOTH SIDES OF THE QUESTION. The question asks
    // about the US OR the UK; the previous version answered with two US clients
    // and the sentence "are both US businesses", which left the UK half
    // unanswered. Renewell Water (UK) closes it, and ORR Protection adds a
    // second US name.
    //
    // No new claim is introduced. Each name is already carried elsewhere on the
    // site or in the catalogue:
    //   Big Jerry's Fencing    case study; "21 cities in 9 U.S. states"
    //   Nurtureful Psychology  case study; "Nurtureful Psychology (US)" in offers.js
    //   ORR Protection         live testimonial on this same page from Sean
    //                          Murphy, "ORR Protection USA" (Reviews.js), and
    //                          "Multi-year support relationships with ORR
    //                          Protection, Big Jerry's" (offers.js, Care Plan)
    //   Renewell Water         "RenewellWater (UK)" (offers.js, offer 07)
    //
    // "Multi-year and still running" is evidenced for ORR Protection and Big
    // Jerry's specifically by the Care Plan proof line, so the sentence keeps
    // "several" rather than claiming it of all four.
    a: <ClientRoll />,
  },
  {
    q: "Who actually works on this — do the senior people disappear after the sale?",
    a:
      "The team that starts your project finishes it, and the founder is involved in every engagement. The names on your kickoff call are the names on your delivery.",
  },
  {
    q: "What if it goes wrong halfway?",
    // ANSWERS THE PREVENTION FIRST, then the remedy. The previous version only
    // covered what happens after something has gone wrong, which reads as
    // though going wrong is expected.
    //
    // The prevention half introduces no new claim: requirement analysis,
    // named sign-off and 48-hour review points are all already stated in
    // src/data/offers.js (weNeed and process on offers 08, 12 and 14).
    //
    // "The hours to put it right are ours" IS a wider commitment than anything
    // previously published — the existing lines cover a slipped date ("we keep
    // working at no extra cost") and defects (the 30-day warranty), not output
    // that differs from what was agreed. Added on instruction.
    //
    // "On fixed-scope work" is load-bearing and MUST stay: it governs both the
    // warranty and the continuation so neither reads as a company-wide date
    // guarantee (§2.8), and components/home/OpenDoor.js cross-references this
    // answer for exactly that qualifier. The two-week cap is deliberate —
    // unbounded continuation is open-ended liability.
    a:
      "Mostly it is prevented rather than fixed. Scope is pinned down in the first requirements session and every decision is confirmed back to you before it is built, so a misunderstanding surfaces at a review rather than at delivery — and no client has had to start over. If something still lands differently from what was agreed, the hours to put it right are ours; on fixed-scope work you also get a 30-day post-launch warranty, and if that date slips for reasons on our side we keep working at no extra cost for up to two weeks.",
  },
  {
    q: "Why is this cheaper than a local agency?",
    // The cost base is now named plainly rather than implied. The first clause
    // is unchanged; the second replaces "you are buying something we have built
    // before rather than funding our learning curve" on instruction.
    //
    // That repeat-build argument is not lost to the site — it is still made,
    // and more concretely, in the Odoo for Manufacturers FAQ in offers.js:
    // "Because we've built this configuration before. The second implementation
    // in a vertical costs us far less than the first, and we pass that on."
    //
    // The closing sentence is deliberate. Naming a lower cost base invites the
    // reader to assume cheaper people, so the seniority claim answers it in the
    // same breath. Note the related guard in offers.js offer 19: never publish
    // an hourly figure — a rate table is a different thing from a cost base.
    a:
      "You are not paying for a sales layer or a local office, and salaries in the region we build from are lower than in the US or UK. The seniority of the people on your project does not change with the rate.",
  },
]

const Objections = () => (
  <section className="hs-obj">
    <div className="hs-obj__inner">
      <span className="hs-obj__eyebrow">Before you ask</span>
      <h2 className="hs-obj__heading">What you are probably thinking</h2>
      <p className="hs-obj__lede">
        You have questions you would not raise on a first call, so here are the
        answers before you ask.
      </p>

      <ul className="hs-obj__list">
        {OBJECTIONS.map((o, i) => (
          <li
            key={o.q}
            className="hs-obj__item"
            data-reveal
            style={{ transitionDelay: `${i * 70}ms` }}
          >
            <p className="hs-obj__q">{o.q}</p>
            <p className="hs-obj__a">{o.a}</p>
          </li>
        ))}
      </ul>
    </div>
  </section>
)

export default memo(Objections)
